/**
 * REST API configuration for the heritage-app-backend.
 *
 * The check-in app historically talked to a GraphQL backend; this module
 * (and the rest of `shared/api/rest`) replaces that with the NestJS REST API
 * already consumed by the heritage mobile app. Endpoint paths mirror
 * `heritage-app/src/api/config.ts` exactly, extended with the children /
 * parents / check-in / teacher / admin routes the check-in app needs.
 */

import { envConfig } from '@/shared/config/envConfig'

const DEFAULT_LOCAL_API = 'http://localhost:3009'

export const API_BASE_URL = envConfig.apiBaseUrl?.replace(/\/$/, '') || DEFAULT_LOCAL_API

const withQuery = (path: string, params: Record<string, string | number | undefined>) => {
    const search = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
            search.append(key, String(value))
        }
    }
    const qs = search.toString()
    return qs ? `${path}?${qs}` : path
}

export const ENDPOINTS = {
    AUTH: {
        LOGIN: '/api/v1/auth/login',
        SIGNUP: '/api/v1/auth/signup',
        REFRESH: '/api/v1/auth/refresh',
        ME: '/api/v1/auth/me',
        FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
        CHANGE_PASSWORD: '/api/v1/auth/change-password',
        DELETE_ACCOUNT: '/api/v1/auth/account'
    },
    BRANCHES: {
        LIST: '/api/v1/branches',
        BY_ID: (id: string) => `/api/v1/branches/${id}`
    },
    CHILDREN: {
        LIST: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/children', params),
        BY_ID: (id: string) => `/api/v1/children/${id}`,
        BY_NAME: (name: string) => withQuery('/api/v1/children/by-name', { name }),
        BY_AGE_GROUP: (ageGroup: string) => `/api/v1/children/by-age-group/${ageGroup}`,
        BY_QR_CODE: (qrCodeData: string) =>
            withQuery('/api/v1/children/by-qr-code', { qrCodeData }),
        BY_DOB: (dateOfBirth: string) =>
            withQuery('/api/v1/children/by-dob', { dateOfBirth }),
        CREATE: '/api/v1/children',
        UPDATE: (id: string) => `/api/v1/children/${id}`,
        DELETE: (id: string) => `/api/v1/children/${id}`,
        BULK_DELETE: (ids: string[]) =>
            withQuery('/api/v1/children', { ids: ids.join(',') }),
        REGENERATE_ALL_QR: '/api/v1/children/qr-codes/regenerate-all',
        REGENERATE_QR_BY_PARENT_EMAIL:
            '/api/v1/children/qr-codes/regenerate-by-parent-email',
        LINK_ALTERNATIVE_CONTACT: (childId: string, altContactId: string) =>
            `/api/v1/children/${childId}/alternative-contacts/${altContactId}`
    },
    PARENTS: {
        LIST: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/parents', params),
        ME: '/api/v1/parents/me',
        ME_CHILDREN: '/api/v1/parents/me/children',
        BY_ID: (id: string) => `/api/v1/parents/${id}`,
        WITH_CO_PARENT: (id: string) => `/api/v1/parents/${id}/with-co-parent`,
        BY_NAME: (name: string) => withQuery('/api/v1/parents/by-name', { name }),
        BY_EMAIL: (email: string) => withQuery('/api/v1/parents/by-email', { email }),
        SIMPLE_SEARCH: (name: string) =>
            withQuery('/api/v1/parents/simple-search', { name }),
        SIGNUP: '/api/v1/parents/signup',
        REQUEST_TEACHER_ROLE: '/api/v1/parents/request-teacher-role',
        REQUEST_PARENT_ROLE: '/api/v1/parents/request-parent-role',
        UPDATE_ME: '/api/v1/parents/me',
        UPDATE: (id: string) => `/api/v1/parents/${id}`,
        DELETE: (id: string) => `/api/v1/parents/${id}`,
        LINK_CHILD: (parentId: string, childId: string) =>
            `/api/v1/parents/${parentId}/children/${childId}`
    },
    CO_PARENTS: {
        ME: '/api/v1/co-parents/me',
        ME_NEW: '/api/v1/co-parents/me/new',
        CHECK: (parentId1: string, parentId2: string) =>
            withQuery('/api/v1/co-parents/check', { parentId1, parentId2 }),
        BY_PARENT_ID: (parentId: string) => `/api/v1/co-parents/${parentId}`,
        CREATE: '/api/v1/co-parents',
        REMOVE: (parentId: string) => `/api/v1/co-parents/${parentId}`
    },
    ALTERNATIVE_CONTACTS: {
        BY_ID: (id: string) => `/api/v1/alternative-contacts/${id}`,
        BY_NAME: (name: string) =>
            withQuery('/api/v1/alternative-contacts/by-name', { name }),
        BY_PARENT: (parentId: string) =>
            `/api/v1/alternative-contacts/by-parent/${parentId}`,
        CREATE: '/api/v1/alternative-contacts',
        UPDATE: (id: string) => `/api/v1/alternative-contacts/${id}`,
        DELETE: (id: string) => `/api/v1/alternative-contacts/${id}`
    },
    CHECKINS: {
        LIST: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/checkins', params),
        AGGREGATES: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/checkins/aggregates', params),
        CHECKOUT_QUEUE: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/checkins/checkout-queue', params),
        STALE: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/checkins/stale', params),
        CHECKOUT_QUEUE_STREAM: '/api/v1/checkins/checkout-queue/stream',
        CHILD: (childId: string) => `/api/v1/checkins/child/${childId}`,
        CHILD_HISTORY: (childId: string) => `/api/v1/checkins/child/${childId}/history`,
        CHECKIN: '/api/v1/checkins/checkin',
        CHECKIN_WITHOUT_QR: '/api/v1/checkins/checkin-without-qr',
        CHECKOUT: '/api/v1/checkins/checkout',
        CHECKOUT_WITHOUT_QR: '/api/v1/checkins/checkout-without-qr',
        SEND_TO_PARENT: (childId: string) => `/api/v1/checkins/${childId}/send-to-parent`,
        CLEAR_QUEUE: '/api/v1/checkins/checkout-queue/clear',
        CLEAR_STALE: '/api/v1/checkins/stale/clear'
    },
    PARENT_DUPLICATES: {
        PENDING: '/api/v1/parent-duplicates/pending',
        PROCESSED: '/api/v1/parent-duplicates/processed',
        BY_PARENT: (
            parentId: string,
            params: Record<string, string | number | undefined> = {}
        ) => withQuery(`/api/v1/parent-duplicates/by-parent/${parentId}`, params),
        BY_ID: (id: string) => `/api/v1/parent-duplicates/${id}`,
        REFRESH: '/api/v1/parent-duplicates/refresh',
        PROCESS: (id: string) => `/api/v1/parent-duplicates/${id}/process`,
        ACCEPT: (id: string) => `/api/v1/parent-duplicates/${id}/accept`,
        REJECT: (id: string) => `/api/v1/parent-duplicates/${id}/reject`,
        KEEP_DUPLICATE: (id: string) => `/api/v1/parent-duplicates/${id}/keep-duplicate`
    },
    TEACHERS: {
        LIST: (params: Record<string, string | number | undefined> = {}) =>
            withQuery('/api/v1/teachers', params),
        ME: '/api/v1/teachers/me',
        BY_ID: (id: string) => `/api/v1/teachers/${id}`,
        BY_BRANCH: (branchId: string) => `/api/v1/teachers/by-branch/${branchId}`,
        CREATE: '/api/v1/teachers',
        UPDATE: (id: string) => `/api/v1/teachers/${id}`,
        APPROVE: (id: string) => `/api/v1/teachers/${id}/approve`,
        REJECT: (id: string) => `/api/v1/teachers/${id}/reject`,
        SUSPEND: (id: string) => `/api/v1/teachers/${id}/suspend`,
        DELETE: (id: string) => `/api/v1/teachers/${id}`
    }
} as const

/** Admin content-management endpoints (`/api/v1/admin/<resource>`). */
export type AdminResource =
    | 'weekly-bundles'
    | 'announcements'
    | 'quotes'
    | 'covenants'
    | 'videos'
    | 'anthems'
    | 'media-items'
    | 'game-categories'
    | 'game-levels'
    | 'game-questions'

export const ADMIN_ENDPOINTS = {
    LIST: (
        resource: AdminResource,
        params: Record<string, string | number | undefined> = {}
    ) => withQuery(`/api/v1/admin/${resource}`, params),
    BY_ID: (resource: AdminResource, id: string) => `/api/v1/admin/${resource}/${id}`,
    CREATE: (resource: AdminResource) => `/api/v1/admin/${resource}`,
    UPDATE: (resource: AdminResource, id: string) => `/api/v1/admin/${resource}/${id}`,
    DELETE: (resource: AdminResource, id: string) => `/api/v1/admin/${resource}/${id}`
}
